import { useEffect, useState } from 'react';
import { Box, CardMedia, CircularProgress, Grid, IconButton, TextField, Typography } from '@material-ui/core';
import { checkFileSizeAvailability, convertBytesToKiloBytes } from 'utils';
import { toastMessages } from 'config/constants';
import { useCompany } from 'contexts/CompanyContext';
import { useFormik } from 'formik';
import { useTheme } from '@material-ui/core/styles';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper';
import * as Yup from 'yup';
import arrowLeft from '_assets/common/arrow_left.svg';
import arrowRight from '_assets/common/arrow_right.svg';
import DefaultButton from 'components/DefaultButton';
import DeleteIcon from '@material-ui/icons/Delete';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import 'swiper/swiper-bundle.min.css';
import toast from 'react-hot-toast';
import uploadLogo from '_assets/parameters/upload.svg';
import useStyles from './styles';
import useWidth from 'hooks/useWidth';

const validationSchema = Yup.object({
  description: Yup.string(),
  link: Yup.string(),
}).defined();

const getInitialFavPicture = ({ main_picture, pictures }) => ({
  id: pictures.find((p) => p.id === main_picture)?.id ?? -1,
  imageURL: pictures.find((p) => p.name === main_picture)?.imageURL ?? '',
  name: main_picture ?? '',
});

const CompanyDetailsForm = ({ onSubmit = () => {}, setSubmitting = () => {}, onCancel = () => {} }) => {
  const classes = useStyles();
  const { palette } = useTheme();

  const { currentCompany, currentCompanyDetails } = useCompany();
  const width = useWidth();

  const [favPicture, setFavPicture] = useState(getInitialFavPicture(currentCompanyDetails));
  const [pictures, setPictures] = useState(currentCompanyDetails?.pictures ?? []);

  const [sliderBtnHover, setSliderBtnHover] = useState(false);

  const {
    errors,
    handleChange,
    handleSubmit,
    isSubmitting,
    resetForm,
    touched,
    values,
    setFieldValue,
    setFieldTouched,
  } = useFormik({
    validationSchema,
    initialValues: {
      description: currentCompanyDetails?.description ?? '',
      link: currentCompanyDetails?.link ?? '',
      picturesToAdd: [],
      picturesToRemove: [],
    },
    onSubmit: async (values) => {
      if (!currentCompany?.id) return;

      const valuesToSend = { ...values, mainPicture: favPicture.name };
      setSubmitting(true);
      const result = await onSubmit(valuesToSend);

      if (!!result) {
        setPictures(result.pictures);
        resetForm({
          values: { description: values.description, link: values.link, picturesToAdd: [], picturesToRemove: [] },
        });
      }
      setSubmitting(false);
    },
  });

  useEffect(() => {
    if (pictures.length > 0) {
      if (!favPicture.name) {
        setFavPicture(pictures[0]);
      } else {
        setFavPicture(
          pictures.find((pic) => pic.name === favPicture.name) ?? getInitialFavPicture(currentCompanyDetails),
        );
      }
    }
  }, [currentCompanyDetails, favPicture.name, pictures]);

  const onPictureChange = async (event) => {
    if (event.target.files != null && event.target.files.length) {
      let files = values.picturesToAdd ?? [];
      let picturesToShow = [...pictures];
      for (const file of event.target.files) {
        // We check if the picture is not already put
        if (!pictures.find((p) => p.name === file.name)) {
          picturesToShow = [
            {
              id: -1,
              name: file.name,
              imageURL: URL.createObjectURL(new Blob([file], { type: 'image/*' })),
              size: convertBytesToKiloBytes(file.size),
            },
            ...picturesToShow,
          ];
          files = [...files, file];
        }
      }

      const status = checkFileSizeAvailability(
        picturesToShow,
        currentCompanyDetails.max_data_size,
        currentCompanyDetails.nb_max_pictures,
      );

      if (status !== 'OK') return toast.error(toastMessages.error[status]);

      setPictures(picturesToShow);
      await setFieldValue('picturesToAdd', files);
      await setFieldTouched('picturesToAdd', true);
      toast.success(event.target.files.length > 1 ? 'Les images ont été ajoutées' : "L'image a été ajoutée");
    }
  };

  const onRemovePicture = (pic) => {
    if (!values.picturesToAdd.find((p) => p.name === pic.name)) {
      setFieldValue('picturesToRemove', [...values.picturesToRemove, pic.id]);
      setFieldTouched('picturesToRemove', true);
    } else {
      setFieldValue(
        'picturesToAdd',
        values.picturesToAdd.filter((p) => p.name !== pic.name),
      );
      if (values.picturesToAdd.length === 0) setFieldTouched('picturesToAdd', false);
    }
    if (pic.name === favPicture.name) {
      setFavPicture({ id: -1, name: '', imageURL: '' });
    }

    setPictures(pictures.filter((p) => p.name !== pic.name));
    toast.success("L'image à été retirée de la selection");
  };

  const onPictureClick = (event) => {
    event.target.value = '';
  };

  const handleCancel = () => {
    if (isSubmitting) return;

    resetForm();
    onCancel();
    setFavPicture(getInitialFavPicture(currentCompanyDetails));
    setPictures(currentCompanyDetails.pictures);
  };

  return (
    <Grid container item justifyContent="center" style={{ flexGrow: 1 }}>
      <form onSubmit={handleSubmit}>
        <Grid container justifyContent="center" style={{ height: '100%' }} className={classes.root}>
          <Grid container item xs={12} md={9} spacing={1} style={{ marginBottom: 8 }}>
            <Grid container item xs={12} spacing={3}>
              <Grid item xs={3} className={classes.descInputContainer}>
                <Typography variant="h4">Album photo</Typography>
                <Typography style={{ fontWeight: 300 }}>Ajouter des photos</Typography>
              </Grid>

              <Grid item xs={9} className={classes.descInputContainer}>
                <Typography variant="h4">Mon Descriptif</Typography>
                <Typography style={{ fontWeight: 300 }}>Présentez-vous en quelques lignes</Typography>
              </Grid>

              <Grid item xs={3}>
                <Box className={classes.imageContainer}>
                  <CardMedia className={classes.cardMedia} image={favPicture.imageURL ?? null} />
                  <input
                    accept=".jpg,.jpeg.,.gif,.png,.mov,.mp4"
                    className={classes.input}
                    onChange={onPictureChange}
                    onClick={onPictureClick}
                    id="postDocumentInput"
                    name="postDocument"
                    type="file"
                    multiple
                  />
                  {!favPicture.imageURL ? (
                    <label className={classes.inputLabelContainer} htmlFor="postDocumentInput">
                      <IconButton color="primary" aria-label="upload document" component="span" size="small">
                        <img alt="Upload" src={uploadLogo} />
                      </IconButton>

                      <Typography className={classes.inputLabel} variant="body2">
                        Ajouter une photo
                      </Typography>
                    </label>
                  ) : (
                    <label className={classes.iconLabelContainer} htmlFor="postDocumentInput">
                      <IconButton
                        className={classes.iconLabel}
                        color="primary"
                        aria-label="upload document"
                        component="span"
                        size="small"
                      >
                        <img alt="Upload" src={uploadLogo} />
                      </IconButton>
                    </label>
                  )}
                </Box>
                <Typography style={{ fontSize: 18, fontWeight: 300, marginTop: '8px' }}>Quota 20 Mo</Typography>
              </Grid>

              <Grid item xs={9}>
                <TextField
                  error={touched.description && Boolean(errors.description)}
                  helperText={touched.description && errors.description}
                  id="description"
                  fullWidth
                  multiline
                  name="description"
                  placeholder="Cette présentation permet aux clients de mieux vous connaitre"
                  variant="outlined"
                  minRows={11}
                  value={values.description}
                  onChange={handleChange}
                  className={classes.textfieldContainer}
                />
              </Grid>
            </Grid>

            <Grid container item xs={12} spacing={3}>
              <Box style={{ width: '80vw', minHeight: 100, maxWidth: '100%', display: 'flex', alignItems: 'center' }}>
                <Box
                  className={classes.sliderButtonBox}
                  onMouseEnter={() => setSliderBtnHover(true)}
                  onMouseLeave={() => setSliderBtnHover(false)}
                >
                  <IconButton className={classes.sliderButton + ' prev-arrow'}>
                    <Box
                      style={{
                        backgroundColor: sliderBtnHover ? palette.primary.main : palette.text.primary,
                        WebkitMaskSize: 'contain',
                        maskPosition: 'center',
                        WebkitMaskPosition: 'center',
                        maskSize: 'contain',
                        WebkitMaskRepeat: 'no-repeat',
                        MaskRepeat: 'no-repeat',
                        WebkitMaskImage: `url(${arrowLeft})`,
                        maskImage: `url(${arrowLeft})`,
                        width: 20,
                        height: 35,
                      }}
                    />
                  </IconButton>
                </Box>

                <Swiper
                  modules={[Navigation]}
                  navigation={{ nextEl: '.next-arrow', prevEl: '.prev-arrow' }}
                  slidesPerView={width === 'xl' ? 8 : width === 'lg' ? 7 : 6}
                  spaceBetween={20}
                  style={{ width: '100%' }}
                >
                  {pictures.map((pic, index) => {
                    return (
                      <SwiperSlide
                        key={'pic-' + index}
                        style={{
                          position: 'relative',
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <CardMedia className={classes.sliderImage} image={pic?.imageURL ?? null} />
                        <Box className={classes.buttonImageContainer}>
                          <IconButton
                            className={classes.iconButton}
                            onClick={() => {
                              setFavPicture(pictures.find((p) => p.name === pic.name));
                              toast.success('Image vitrine selectionnée');
                            }}
                          >
                            {pic.imageURL === favPicture.imageURL ? (
                              <StarIcon fontSize="large" />
                            ) : (
                              <StarBorderIcon fontSize="large" />
                            )}
                          </IconButton>
                          <IconButton className={classes.iconButton} onClick={() => onRemovePicture(pic)}>
                            <DeleteIcon fontSize="large" />
                          </IconButton>
                        </Box>
                      </SwiperSlide>
                    );
                  })}
                </Swiper>

                <Box
                  className={classes.sliderButtonBox}
                  onMouseEnter={() => setSliderBtnHover(true)}
                  onMouseLeave={() => setSliderBtnHover(false)}
                >
                  <IconButton className={classes.sliderButton + ' next-arrow'}>
                    <Box
                      style={{
                        backgroundColor: sliderBtnHover ? palette.primary.main : palette.text.primary,
                        WebkitMaskSize: 'contain',
                        maskPosition: 'center',
                        WebkitMaskPosition: 'center',
                        maskSize: 'contain',
                        WebkitMaskRepeat: 'no-repeat',
                        MaskRepeat: 'no-repeat',
                        WebkitMaskImage: `url(${arrowRight})`,
                        maskImage: `url(${arrowRight})`,
                        width: 20,
                        height: 35,
                      }}
                    />
                  </IconButton>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h4">Notation Tripadvisor / Google</Typography>
              <Typography style={{ fontWeight: 300 }}>
                Je dispose d’une notation Tripadvisor ou Google pour mettre en avant mon activité
              </Typography>
              <TextField
                error={touched.link && Boolean(errors.link)}
                helperText={touched.link && errors.link}
                fullWidth
                id="link"
                name="link"
                placeholder="Copier le lien vers mon compte Tripadvisor ou Google"
                variant="outlined"
                value={values.link}
                onChange={handleChange}
                style={{ padding: 8 }}
              />
            </Grid>
          </Grid>

          <Grid container item xs={12} justifyContent="center">
            <Grid container item xs={12} alignItems="flex-end" justifyContent="center" style={{ marginBottom: '16px' }}>
              <DefaultButton cancel disabled={isSubmitting} onClick={handleCancel}>
                Annuler
              </DefaultButton>
              <DefaultButton color="secondary" disabled={isSubmitting} type="submit" variant="contained">
                {isSubmitting ? <CircularProgress size={20} /> : 'Valider'}
              </DefaultButton>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </Grid>
  );
};

export default CompanyDetailsForm;
